import {
  Change_Source,
  LocationAttributeCreate,
  RetailerNetworkLocationFragment,
  RetailerNetworkLocationGroupFragment,
} from '@occc/graphql';
import { getStateSingleAutocompleteInitialValues } from '@occc-app/design-system';
import { parseDate, DateTimeFormatParse } from '@occc-app/lib/date';

import {
  GeneralDetailsFormInitialValues,
  GeneralDetailsFormValues,
} from './retailer-network-my-stores-store-v2-update-page.types';

import {
  LOCATION_ATTRIBUTE_VALUE,
  ScheduleValues,
} from '../retailer-network-location-update-page/retailer-network-location-update-page.types';
import {
  clearTime,
  getOperationHoursFromFormValues,
} from '../retailer-network-location-update-page/retailer-network-location-update-page.utils';

type Location = RetailerNetworkLocationFragment['location'];

export const getGeneralDetailsInitialValues = (
  location: Location | undefined,
  locationGroup?: RetailerNetworkLocationGroupFragment | null,
): GeneralDetailsFormInitialValues => {
  const availableServices = (location?.attributes ?? [])
    .filter((attribute) => attribute.value === LOCATION_ATTRIBUTE_VALUE.YES)
    .map((attribute) => attribute.name);

  const locationHoursOfOperation = location?.hoursOfOperationGroup?.hours ?? [];
  const schedule = locationHoursOfOperation.reduce<ScheduleValues>(
    (acc, operationDay) => ({
      ...acc,
      [operationDay.dayOfWeek]: {
        start: parseDate(clearTime(operationDay.openTime), DateTimeFormatParse.Time24h),
        end: parseDate(clearTime(operationDay.closeTime), DateTimeFormatParse.Time24h),
        isOpenAllDay: Boolean(
          operationDay.openTime && operationDay.openTime === operationDay.closeTime,
        ),
      },
    }),
    {},
  );
  const availableDays = locationHoursOfOperation.map((operationDay) => operationDay.dayOfWeek);

  return {
    availableDays,
    schedule,
    details: {
      name: location?.name || '',
      locationGroup: locationGroup ?? null,
      street1: location?.address?.street1 || '',
      street2: location?.address?.street2 || '',
      city: location?.address?.city || '',
      state: location?.address
        ? getStateSingleAutocompleteInitialValues(location.address.state)
        : undefined,
      zipCode: location?.address?.zipCode || '',
      phoneNumber: location?.phoneNumbers[0]?.phoneNumber || undefined,
      availableServices,
      brandingDetails: location?.brandingDetails
        ? {
            primaryColor: location.brandingDetails.primaryColor || null,
            secondaryColor: location.brandingDetails.secondaryColor || null,
          }
        : null,
    },
  };
};

export const prepareGeneralDetailsInputs = (
  locationId: string,
  values: GeneralDetailsFormValues,
) => {
  if (!values.details.state) {
    return null;
  }

  const attributes: LocationAttributeCreate[] = (values.details.availableServices || []).map(
    (attribute) => ({ name: attribute, value: LOCATION_ATTRIBUTE_VALUE.YES }),
  );

  return {
    variables: {
      locationUpdateInput: {
        uuid: locationId,
        changeSource: Change_Source.CommandCenter,
        name: values.details.name,
        phoneNumbers: values.details.phoneNumber ? [values.details.phoneNumber] : [],
        address: {
          street1: values.details.street1,
          street2: values.details.street2 || null,
          city: values.details.city,
          zipCode: values.details.zipCode,
          state: values.details.state.id,
        },
        attributes,
        hoursOfOperationGroup: getOperationHoursFromFormValues(
          values.schedule,
          values.availableDays,
        ),
        brandingDetails:
          values.details.brandingDetails?.primaryColor ||
          values.details.brandingDetails?.secondaryColor
            ? {
                primaryColor: values.details.brandingDetails?.primaryColor || null,
                secondaryColor: values.details.brandingDetails?.secondaryColor || null,
              }
            : null,
      },
    },
  };
};

export type LocationGroupMutationChanges = {
  locationGroupUuidsToRemove: string[];
  locationGroupUuidToAdd?: string;
};

export const getLocationGroupMutationChanges = (
  previousLocationGroupUuids: (string | undefined | null)[] | undefined,
  nextLocationGroupUuid: string | undefined | null,
): LocationGroupMutationChanges => {
  const previousUuids = (previousLocationGroupUuids || []).filter((uuid): uuid is string =>
    Boolean(uuid),
  );

  return {
    locationGroupUuidsToRemove: previousUuids.filter((uuid) => uuid !== nextLocationGroupUuid),
    locationGroupUuidToAdd:
      nextLocationGroupUuid && !previousUuids.includes(nextLocationGroupUuid)
        ? nextLocationGroupUuid
        : undefined,
  };
};
